/**
 * Base_Conocimiento_RAG (demo client-side) de la Capa Operacional AI-First.
 *
 * Implementa la interfaz {@link RAG} de `../api/contracts` para la variante
 * **demo** (client-side, R17.1/R17.5), sin servicios externos ni backend:
 * embeddings **pre-computados de forma determinista** a partir del texto
 * (hashing de términos a un vector de frecuencias no negativo) + **similitud
 * coseno pura**. La Base_Conocimiento_RAG:
 *
 *   - **separa** conocimiento estructurado (Postgres, series de tiempo,
 *     Redis, graph DB) de conocimiento no estructurado (evidencias, PDFs,
 *     reportes, comentarios, auditorías, bitácoras) (R13.1); solo el
 *     conocimiento no estructurado se trocea y se indexa para recuperación
 *     semántica (R13.2);
 *   - al incorporar un documento no estructurado lo **chunkea**, asocia a
 *     cada Chunk su **embedding** pre-computado y **enriquece** los
 *     metadatos de origen `{entidad, tipo, fecha, autor}` (R13.2);
 *   - indexa de forma **atómica**: si la indexación de un documento no
 *     puede completarse, **aborta** el documento, **no persiste Chunks
 *     parciales** y **registra** el fallo en la Observabilidad (R13.7);
 *   - en `retrieve(query, umbral, k=10)` devuelve los resultados cuya
 *     similitud coseno `∈ [0, 1]` es `≥ umbral`, **ordenados de forma
 *     descendente**, **limitados a `k`**, cada uno con su `score` y su
 *     referencia de origen; si ninguno alcanza el umbral, devuelve el
 *     **conjunto vacío** y no incluye resultados por debajo del umbral
 *     (R13.3, R13.6);
 *   - en `getChunk(chunk_id)` preserva el **round-trip** de los metadatos:
 *     los campos de origen recuperados son idénticos campo por campo a los
 *     registrados al indexar (R13.5).
 *
 * Las funciones puras {@link embedText}, {@link cosineSimilarity},
 * {@link chunkText}, {@link chunkDocumento}, {@link clasificarDocumento} y
 * {@link rankResultados} se exportan para que las tareas de testing PBT
 * (Properties 34, 35, 36) las ejerciten sin instanciar la clase ni
 * depender de IndexedDB.
 */

import type {
  Chunk,
  Documento,
  Id,
  IndexResult,
  MetadatosChunk,
  RAG,
  ResultadoRecuperacion,
  Result,
} from "../api/contracts";

// ---------------------------------------------------------------------------
// Configuración y clasificación structured / unstructured (R13.1)
// ---------------------------------------------------------------------------

/** Dimensión por defecto del vector de embedding (buckets de hashing). */
export const EMBEDDING_DIM = 96;

/** Tamaño por defecto (en términos) de cada Chunk de un documento. */
export const CHUNK_SIZE_TERMINOS = 40;

/** Máximo por defecto de resultados de una Recuperacion_Semantica (R13.3). */
export const RETRIEVE_K_DEFAULT = 10;

/**
 * Tipos de origen considerados **conocimiento estructurado** (R13.1):
 * Postgres, series de tiempo, Redis, graph DB. El conocimiento
 * estructurado **no** se trocea ni se indexa en el vector store; se
 * mantiene separado del no estructurado.
 */
export const TIPOS_ESTRUCTURADOS: ReadonlySet<string> = new Set([
  "postgres",
  "serie_tiempo",
  "series",
  "redis",
  "graph",
  "graph_db",
  "tabla",
  "kpi",
  "metrica",
]);

/** Naturaleza del conocimiento de un documento (R13.1). */
export type NaturalezaDocumento = "estructurado" | "no_estructurado";

/**
 * Clasifica un documento como conocimiento estructurado o no estructurado
 * a partir del tipo declarado en sus metadatos de origen (R13.1). El cotejo
 * es **case-insensitive**; cualquier tipo no reconocido como estructurado
 * se considera no estructurado (evidencias, PDFs, reportes, comentarios,
 * auditorías, bitácoras).
 */
export function clasificarDocumento(documento: Documento): NaturalezaDocumento {
  const tipo = (documento.metadatos?.tipo ?? "").trim().toLowerCase();
  return TIPOS_ESTRUCTURADOS.has(tipo) ? "estructurado" : "no_estructurado";
}

// ---------------------------------------------------------------------------
// Tokenización y embeddings deterministas (hashing de términos)
// ---------------------------------------------------------------------------

/**
 * Tokeniza un texto en términos en minúsculas: separa por cualquier
 * carácter que no sea alfanumérico (incluye acentos latinos básicos) y
 * descarta los vacíos. Determinista y sin estado.
 */
export function tokenize(texto: string): string[] {
  if (!texto) return [];
  return texto
    .toLowerCase()
    .split(/[^0-9a-záéíóúüñ]+/i)
    .filter((t) => t.length > 0);
}

/** Hash FNV-1a de 32 bits de una cadena (determinista, no negativo). */
function fnv1a(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    // h *= 16777619 con aritmética de 32 bits sin signo.
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * `embedText`: embedding **pre-computado determinista** de un texto, sin
 * servicio externo. Cada término se hashea a un bucket de `[0, dim)` y se
 * acumula su frecuencia, produciendo un vector de **componentes no
 * negativas**. Al ser no negativo, la similitud coseno con cualquier otro
 * embedding generado por esta misma función queda acotada a `[0, 1]`
 * (R13.2, R13.3).
 *
 * El mismo texto produce siempre el mismo vector (determinismo); textos con
 * términos compartidos producen vectores con mayor solapamiento y, por
 * tanto, mayor similitud.
 */
export function embedText(texto: string, dim: number = EMBEDDING_DIM): number[] {
  const d = Number.isInteger(dim) && dim > 0 ? dim : EMBEDDING_DIM;
  const v = new Array<number>(d).fill(0);
  for (const term of tokenize(texto)) {
    const bucket = fnv1a(term) % d;
    v[bucket] += 1;
  }
  return v;
}

/**
 * `cosineSimilarity`: similitud coseno entre dos vectores, **acotada al
 * rango `[0, 1]`** (R13.3). Si alguno de los vectores es el vector cero o
 * las dimensiones no coinciden, la similitud es 0. Los valores fuera de
 * `[0, 1]` por error de redondeo en coma flotante o por componentes
 * negativas se recortan al rango.
 */
export function cosineSimilarity(a: number[], b: number[]): number {
  if (!a || !b || a.length === 0 || a.length !== b.length) return 0;
  let dot = 0;
  let na = 0;
  let nb = 0;
  for (let i = 0; i < a.length; i++) {
    dot += a[i] * b[i];
    na += a[i] * a[i];
    nb += b[i] * b[i];
  }
  if (na === 0 || nb === 0) return 0;
  const cos = dot / (Math.sqrt(na) * Math.sqrt(nb));
  if (!Number.isFinite(cos)) return 0;
  return cos < 0 ? 0 : cos > 1 ? 1 : cos;
}

// ---------------------------------------------------------------------------
// Chunking de documentos no estructurados (R13.2)
// ---------------------------------------------------------------------------

/**
 * Divide un texto en fragmentos de a lo sumo `tam` términos cada uno,
 * preservando el orden. Determinista. Un texto vacío (sin términos)
 * produce un único Chunk con el texto original recortado, de modo que un
 * documento no estructurado siempre genere al menos un Chunk indexable.
 */
export function chunkText(texto: string, tam: number = CHUNK_SIZE_TERMINOS): string[] {
  const limite = Number.isInteger(tam) && tam > 0 ? tam : CHUNK_SIZE_TERMINOS;
  const terminos = (texto ?? "").trim().split(/\s+/).filter((t) => t.length > 0);
  if (terminos.length === 0) return [(texto ?? "").trim()];
  const chunks: string[] = [];
  for (let i = 0; i < terminos.length; i += limite) {
    chunks.push(terminos.slice(i, i + limite).join(" "));
  }
  return chunks;
}

/** Identificador determinista de un Chunk dentro de su documento. */
export function chunkId(documento_id: Id, indice: number): Id {
  return `${documento_id}#${indice}`;
}

/** Copia profunda de los metadatos de origen para garantizar el round-trip exacto (R13.5). */
function clonarMetadatos(m: MetadatosChunk): MetadatosChunk {
  return {
    entidad: { tipo: m.entidad.tipo, id: m.entidad.id },
    tipo: m.tipo,
    fecha: m.fecha,
    autor: m.autor,
  };
}

/**
 * Trocea un documento no estructurado en Chunks, asociando a cada uno su
 * **embedding** pre-computado y **enriqueciendo** sus metadatos de origen
 * `{entidad, tipo, fecha, autor}` copiados del documento (R13.2). Función
 * pura: no persiste nada.
 *
 * @param embedFn Generador de embeddings inyectable (por defecto {@link embedText}).
 *                Permite forzar fallos en pruebas de atomicidad (R13.7).
 */
export function chunkDocumento(
  documento: Documento,
  tam: number = CHUNK_SIZE_TERMINOS,
  embedFn: (texto: string) => number[] = embedText,
): Chunk[] {
  const fragmentos = chunkText(documento.contenido, tam);
  return fragmentos.map((texto, i) => ({
    chunk_id: chunkId(documento.documento_id, i),
    documento_id: documento.documento_id,
    texto,
    embedding: embedFn(texto),
    metadatos: clonarMetadatos(documento.metadatos),
  }));
}

// ---------------------------------------------------------------------------
// Ranking de la Recuperacion_Semantica (R13.3, R13.6)
// ---------------------------------------------------------------------------

/**
 * Calcula, filtra, ordena y acota los resultados de una recuperación
 * semántica sobre un conjunto de Chunks dado el embedding de la consulta
 * (R13.3, R13.6). Función pura:
 *
 *   1. calcula la similitud coseno `∈ [0, 1]` de cada Chunk con la consulta;
 *   2. **filtra** los que alcanzan `score ≥ umbral` (R13.3); si ninguno lo
 *      hace, devuelve el conjunto **vacío** (R13.6);
 *   3. **ordena** de forma descendente por `score`, desempatando de forma
 *      **determinista** por `chunk_id` ascendente;
 *   4. **limita** a `k` resultados (k ≤ 0 → ninguno).
 *
 * Cada resultado se devuelve con su `chunk` (que incluye la referencia de
 * origen en `metadatos.entidad`) y su `score`.
 */
export function rankResultados(
  chunks: Chunk[],
  queryEmbedding: number[],
  umbral: number,
  k: number = RETRIEVE_K_DEFAULT,
): ResultadoRecuperacion[] {
  const limite = Number.isFinite(k) ? Math.floor(k) : RETRIEVE_K_DEFAULT;
  if (limite <= 0) return [];

  const candidatos = chunks
    .map((chunk) => ({ chunk, score: cosineSimilarity(chunk.embedding, queryEmbedding) }))
    .filter((r) => r.score >= umbral);

  candidatos.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    // Desempate determinista por identificador de Chunk ascendente.
    return a.chunk.chunk_id < b.chunk.chunk_id ? -1 : a.chunk.chunk_id > b.chunk.chunk_id ? 1 : 0;
  });

  return candidatos.slice(0, limite);
}

// ---------------------------------------------------------------------------
// Observabilidad — registro de fallos de indexación (R13.7)
// ---------------------------------------------------------------------------

/** Fallo de indexación de un documento, registrado en la Observabilidad (R13.7). */
export interface FalloIndexacion {
  documento_id: Id;
  motivo: string;
}

/**
 * Hook a la Observabilidad invocado cuando la indexación de un documento no
 * puede completarse (R13.7). La integración real con el subsistema de
 * Observabilidad se cablea en una tarea posterior; aquí se deja el punto de
 * extensión, con un no-op por defecto para no acoplar este módulo.
 */
export type HookFalloIndexacion = (fallo: FalloIndexacion) => void;

// ---------------------------------------------------------------------------
// RAGLocal — implementación demo de la interfaz RAG (R13)
// ---------------------------------------------------------------------------

/** Opciones de construcción del {@link RAGLocal}. */
export interface RAGLocalOptions {
  /** Dimensión del embedding (por defecto {@link EMBEDDING_DIM}). */
  dim?: number;
  /** Términos por Chunk (por defecto {@link CHUNK_SIZE_TERMINOS}). */
  chunkSize?: number;
  /**
   * Generador de embeddings inyectable (por defecto {@link embedText}).
   * Si lanza una excepción durante la indexación, la operación se aborta
   * de forma atómica sin persistir Chunks parciales (R13.7).
   */
  embedFn?: (texto: string, dim?: number) => number[];
  /** Hook a la Observabilidad para registrar fallos de indexación (R13.7). */
  onIndexFailure?: HookFalloIndexacion;
}

/**
 * Base_Conocimiento_RAG client-side que implementa {@link RAG}. Mantiene en
 * memoria el corpus indexado: los Chunks por `chunk_id` y la lista de
 * Chunks por documento. La indexación es **atómica** (R13.7): construye
 * todos los Chunks de un documento en una zona temporal y solo los
 * **publica** al corpus si la construcción completa tiene éxito; ante
 * cualquier fallo, no persiste nada y registra el fallo en la Observabilidad.
 */
export class RAGLocal implements RAG {
  private readonly dim: number;
  private readonly chunkSize: number;
  private readonly embedFn: (texto: string, dim?: number) => number[];
  private readonly onIndexFailure: HookFalloIndexacion;

  /** Corpus indexado: Chunk por identificador. */
  private readonly chunks = new Map<Id, Chunk>();
  /** Chunks por documento (para reindexación atómica y diagnóstico). */
  private readonly porDocumento = new Map<Id, Id[]>();
  /** Documentos estructurados separados, no indexados en el vector store (R13.1). */
  private readonly estructurados = new Map<Id, Documento>();

  constructor(opts: RAGLocalOptions = {}) {
    this.dim = opts.dim && opts.dim > 0 ? opts.dim : EMBEDDING_DIM;
    this.chunkSize = opts.chunkSize && opts.chunkSize > 0 ? opts.chunkSize : CHUNK_SIZE_TERMINOS;
    this.embedFn = opts.embedFn ?? embedText;
    this.onIndexFailure = opts.onIndexFailure ?? (() => {});
  }

  /**
   * Indexa un documento de forma **atómica** (R13.1, R13.2, R13.7):
   *
   *   - separa el conocimiento estructurado del no estructurado (R13.1);
   *     el estructurado se almacena aparte y **no** se trocea ni se indexa;
   *   - el no estructurado se **chunkea**, se le asocian **embeddings** y se
   *     **enriquecen** los metadatos de origen (R13.2);
   *   - construye todos los Chunks en una zona temporal y solo los publica
   *     si la construcción completa tiene éxito; ante cualquier fallo,
   *     **no persiste Chunks parciales**, deja el corpus intacto y registra
   *     el fallo en la Observabilidad (R13.7).
   */
  async index(documento: Documento): Promise<Result<IndexResult>> {
    try {
      this.validarDocumento(documento);

      if (clasificarDocumento(documento) === "estructurado") {
        // Conocimiento estructurado: separado, sin chunking ni embeddings (R13.1).
        this.estructurados.set(documento.documento_id, documento);
        return { ok: true, value: { documento_id: documento.documento_id, chunks_indexados: 0 } };
      }

      // Construcción en zona temporal (atomicidad, R13.7).
      const nuevos = chunkDocumento(documento, this.chunkSize, (t) => this.embedFn(t, this.dim));
      this.validarChunks(nuevos);

      // Publicación atómica: reemplaza por completo los Chunks previos del
      // documento (reindexación) sin dejar fragmentos parciales.
      this.purgarDocumento(documento.documento_id);
      const ids: Id[] = [];
      for (const chunk of nuevos) {
        this.chunks.set(chunk.chunk_id, chunk);
        ids.push(chunk.chunk_id);
      }
      this.porDocumento.set(documento.documento_id, ids);

      return { ok: true, value: { documento_id: documento.documento_id, chunks_indexados: ids.length } };
    } catch (err) {
      const motivo = err instanceof Error ? err.message : String(err);
      // Atomicidad: el corpus no se ha modificado (la publicación es el
      // último paso). Registrar el fallo en la Observabilidad (R13.7).
      this.onIndexFailure({ documento_id: documento?.documento_id ?? "desconocido", motivo });
      return {
        ok: false,
        error: `Indexación abortada para el documento '${documento?.documento_id ?? "desconocido"}': ${motivo}. No se persistieron Chunks parciales (R13.7).`,
        degraded: false,
      };
    }
  }

  /**
   * Recuperación semántica filtrada, ordenada y acotada por `k` (R13.3,
   * R13.6). Genera el embedding de la consulta y delega el ranking en
   * {@link rankResultados}; si ningún Chunk alcanza el umbral, devuelve el
   * conjunto vacío sin incluir resultados por debajo del umbral (R13.6).
   */
  async retrieve(
    query: string,
    umbral: number,
    k: number = RETRIEVE_K_DEFAULT,
  ): Promise<Result<ResultadoRecuperacion[]>> {
    try {
      const queryEmbedding = this.embedFn(query, this.dim);
      const resultados = rankResultados([...this.chunks.values()], queryEmbedding, umbral, k);
      // Defensa en profundidad: devolver clones para que el llamador no
      // pueda mutar el corpus indexado (preserva el round-trip, R13.5).
      return { ok: true, value: resultados.map((r) => ({ chunk: this.clonarChunk(r.chunk), score: r.score })) };
    } catch (err) {
      const motivo = err instanceof Error ? err.message : String(err);
      return { ok: false, error: `No se pudo ejecutar la recuperación semántica: ${motivo}`, degraded: true };
    }
  }

  /**
   * Recupera un Chunk preservando el **round-trip exacto** de sus metadatos
   * de origen `{entidad, tipo, fecha, autor}` campo por campo (R13.5).
   * Devuelve una copia para que el corpus indexado permanezca inmutable.
   */
  async getChunk(chunk_id: Id): Promise<Result<Chunk>> {
    const chunk = this.chunks.get(chunk_id);
    if (!chunk) {
      return { ok: false, error: `Chunk no encontrado: '${chunk_id}'`, degraded: false };
    }
    return { ok: true, value: this.clonarChunk(chunk) };
  }

  // -------------------------------------------------------------------------
  // Utilidades internas
  // -------------------------------------------------------------------------

  /** Valida la estructura mínima de un documento antes de indexar (R13.7). */
  private validarDocumento(documento: Documento): void {
    if (!documento || typeof documento !== "object") {
      throw new Error("documento ausente o inválido");
    }
    if (!documento.documento_id) {
      throw new Error("documento sin 'documento_id'");
    }
    const m = documento.metadatos;
    if (!m || !m.entidad || !m.entidad.id || !m.entidad.tipo || typeof m.tipo !== "string" ||
        typeof m.fecha !== "string" || typeof m.autor !== "string") {
      throw new Error("metadatos de origen incompletos (entidad, tipo, fecha, autor)");
    }
  }

  /** Valida que todos los Chunks construidos tengan embedding utilizable (R13.7). */
  private validarChunks(chunks: Chunk[]): void {
    for (const c of chunks) {
      if (!Array.isArray(c.embedding) || c.embedding.length === 0) {
        throw new Error(`embedding inválido para el Chunk '${c.chunk_id}'`);
      }
      if (c.embedding.some((x) => typeof x !== "number" || !Number.isFinite(x))) {
        throw new Error(`embedding con valores no finitos en el Chunk '${c.chunk_id}'`);
      }
    }
  }

  /** Elimina del corpus los Chunks de un documento (reindexación atómica). */
  private purgarDocumento(documento_id: Id): void {
    const previos = this.porDocumento.get(documento_id);
    if (previos) {
      for (const id of previos) this.chunks.delete(id);
      this.porDocumento.delete(documento_id);
    }
  }

  /** Clona un Chunk en profundidad (embedding y metadatos) para inmutabilidad. */
  private clonarChunk(chunk: Chunk): Chunk {
    return {
      chunk_id: chunk.chunk_id,
      documento_id: chunk.documento_id,
      texto: chunk.texto,
      embedding: [...chunk.embedding],
      metadatos: clonarMetadatos(chunk.metadatos),
    };
  }

  /** Número de Chunks indexados (diagnóstico/test). */
  size(): number {
    return this.chunks.size;
  }

  /** Número de documentos estructurados separados (diagnóstico/test). */
  sizeEstructurados(): number {
    return this.estructurados.size;
  }
}
